/**
 * AssistedProgramBuilder.jsx — Phase 3 : "Création manuelle assistée (débutant)". Démarre par le
 * questionnaire guidé (ProgramWizard, thème émeraude), puis suggère un programme complet (split +
 * exercices + séries/reps/repos) de manière purement algorithmique (pas d'IA, pas de crédit) —
 * le client peut décocher/échanger chaque exercice avant de créer son programme.
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Wand2, RefreshCcw, Check, Loader2, Dumbbell, Sparkles, ListChecks, Wrench, BookOpen, Clock, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { ProgramWizard } from './ProgramWizard';
import { CustomBeginnerFlow } from './CustomBeginnerFlow';
import { LibraryPickerModal } from './LibraryPickerModal';
import { ExerciseMedia } from './ExerciseLibraryUI';
import { buildSessionForTarget, TOLERANCE_MINUTES } from './exerciseSelectionEngine';
import { estimateSessionMinutes } from './sessionTimeEstimator';

const FULL = ['Quadriceps', 'Dos', 'Pectoraux', 'Épaules', 'Abdominaux'];
const UPPER = ['Pectoraux', 'Dos', 'Épaules', 'Biceps', 'Triceps'];
const LOWER = ['Quadriceps', 'Ischio-jambiers', 'Fessiers', 'Mollets'];
const PUSH = ['Pectoraux', 'Épaules', 'Triceps'];
const PULL = ['Dos', 'Biceps'];

const SPLITS = {
  1: [{ label: 'Full-Body', groups: FULL }],
  2: [{ label: 'Full-Body A', groups: FULL }, { label: 'Full-Body B', groups: FULL }],
  3: [{ label: 'Full-Body A', groups: FULL }, { label: 'Full-Body B', groups: FULL }, { label: 'Full-Body C', groups: FULL }],
  4: [{ label: 'Haut du corps A', groups: UPPER }, { label: 'Bas du corps A', groups: LOWER }, { label: 'Haut du corps B', groups: UPPER }, { label: 'Bas du corps B', groups: LOWER }],
  5: [{ label: 'Push (Pecs/Épaules/Triceps)', groups: PUSH }, { label: 'Pull (Dos/Biceps)', groups: PULL }, { label: 'Jambes', groups: LOWER }, { label: 'Haut du corps', groups: UPPER }, { label: 'Abdos', groups: ['Abdominaux'] }],
  6: [{ label: 'Push A', groups: PUSH }, { label: 'Pull A', groups: PULL }, { label: 'Jambes A', groups: LOWER }, { label: 'Push B', groups: PUSH }, { label: 'Pull B', groups: PULL }, { label: 'Jambes B', groups: LOWER }],
  7: [{ label: 'Push A', groups: PUSH }, { label: 'Pull A', groups: PULL }, { label: 'Jambes A', groups: LOWER }, { label: 'Push B', groups: PUSH }, { label: 'Pull B', groups: PULL }, { label: 'Jambes B', groups: LOWER }, { label: 'Abdominaux', groups: ['Abdominaux'] }],
};

export { GOAL_PARAMS } from './goalParams';
import { GOAL_PARAMS } from './goalParams';

// La bibliothèque riche (`/api/exercises`) utilise des slugs matériel différents des labels
// affichés dans ProgramWizard (`equipmentTags`) — table de correspondance pour filtrer juste.
const EQUIPMENT_TAG_TO_SLUG = { 'Haltère': 'halteres', 'Kettlebell': 'petit_materiel', 'Barre': 'barre_libre', 'Accessoire': 'petit_materiel' };
const ALL_EQUIPMENT_SLUGS = ['machine_guidee', 'poulie', 'barre_libre', 'halteres', 'poids_du_corps', 'petit_materiel'];

const buildDraft = (wizardData, library) => {
  const split = SPLITS[wizardData.daysPerWeek] || SPLITS[3];
  const allowAllEquipment = wizardData.location === 'Salle de fitness';
  const allowedSlugs = allowAllEquipment
    ? ALL_EQUIPMENT_SLUGS
    : ['poids_du_corps', ...new Set((wizardData.equipmentTags || []).map((t) => EQUIPMENT_TAG_TO_SLUG[t]).filter(Boolean))];
  const excludedGroups = wizardData.injuryGroups || [];
  const usedNamesGlobal = new Set();
  const targetMinutes = wizardData.sessionDurationMinutes || 45;

  return split.map((s) => {
    const groups = s.groups.filter((g) => !excludedGroups.includes(g));
    const result = buildSessionForTarget({
      groups, equipment: allowedSlugs, targetMinutes, goal: wizardData.goal, library: library.exercises, excludedGroups, usedNamesGlobal,
    });
    result.exercises.forEach((e) => usedNamesGlobal.add(e.name));
    const candidatesByGroup = {};
    groups.forEach((g) => {
      candidatesByGroup[g] = library.exercises
        .filter((e) => e.muscle_group === g && allowedSlugs.includes(e.equipment))
        .map((e) => ({
          name: e.name_fr || e.name, muscle_group: e.muscle_group, equipment: e.equipment, exercise_id: e.id,
          image_url: e.image_url, video_url: e.video_status === 'ready' ? e.video_url : null,
          video_status: e.video_status, video_disabled: e.video_disabled, execution: e.execution, tips: e.tips,
        }));
    });
    const exercises = result.exercises.map((e) => {
      const candidates = candidatesByGroup[e.muscle_group] || [];
      return { ...e, included: true, _candidates: candidates, _candidateIdx: candidates.findIndex((c) => c.name === e.name) };
    });
    return {
      session_label: s.label, sessions_target: wizardData.durationWeeks, exercises,
      targetMinutes, insufficientPool: result.insufficientPool,
    };
  });
};

export const AssistedProgramBuilder = ({ en, onBack, onCreated }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [phase, setPhase] = useState('wizard'); // wizard | mode-choice | review | custom
  const [library, setLibrary] = useState({ muscle_groups: [], exercises: [] });
  const [wizardData, setWizardData] = useState(null);
  const [draft, setDraft] = useState([]);
  const [programName, setProgramName] = useState('');
  const [busy, setBusy] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // sIdx

  useEffect(() => {
    authFetch('/api/exercises?limit=1000').then((r) => r.json()).then(setLibrary).catch(() => {});
  }, [authFetch]);

  const onWizardComplete = (data) => {
    setWizardData(data);
    setProgramName(`${data.goal} · ${data.daysPerWeek}x/${L('semaine', 'week')}`);
    setPhase('mode-choice');
  };

  const chooseSuggested = () => {
    setDraft(buildDraft(wizardData, library));
    setPhase('review');
  };

  const toggleExercise = (sIdx, uid) => {
    setDraft((prev) => prev.map((s, i) => i !== sIdx ? s : {
      ...s, exercises: s.exercises.map((e) => e._uid === uid ? { ...e, included: !e.included } : e),
    }));
  };

  const swapExercise = (sIdx, uid) => {
    setDraft((prev) => prev.map((s, i) => {
      if (i !== sIdx) return s;
      const usedNames = new Set(s.exercises.filter((e) => e._uid !== uid).map((e) => e.name));
      return {
        ...s, exercises: s.exercises.map((e) => {
          if (e._uid !== uid || !e._candidates.length) return e;
          let idx = e._candidateIdx;
          for (let tries = 0; tries < e._candidates.length; tries += 1) {
            idx = (idx + 1) % e._candidates.length;
            const cand = e._candidates[idx];
            if (!usedNames.has(cand.name)) {
              return {
                ...e, name: cand.name, equipment: cand.equipment, _candidateIdx: idx,
                exercise_id: cand.exercise_id, image_url: cand.image_url, video_url: cand.video_url,
                video_status: cand.video_status, video_disabled: cand.video_disabled, execution: cand.execution, tips: cand.tips,
              };
            }
          }
          return e;
        }),
      };
    }));
  };

  const addExercise = (sIdx, picked) => {
    const params = GOAL_PARAMS[wizardData.goal] || { sets: 3, reps: '10-12', rest: 60 };
    setDraft((prev) => prev.map((s, i) => i !== sIdx ? s : {
      ...s, exercises: [...s.exercises, {
        _uid: `${s.session_label}-custom-${Date.now()}`,
        name: picked.name, muscle_group: picked.muscle_group, equipment: picked.equipment || '',
        sets_count: params.sets, reps_scheme: params.reps, is_time_based: false, rest_seconds: params.rest,
        included: true, _candidates: [], _candidateIdx: -1,
      }],
    }));
    setPickerFor(null);
  };

  const createProgram = async () => {
    if (!programName.trim()) { toast.error(L('Le nom du programme est requis.', 'Program name is required.')); return; }
    setBusy(true);
    try {
      const sessions = draft.map((s) => ({
        session_label: s.session_label, sessions_target: s.sessions_target,
        exercises: s.exercises.filter((e) => e.included).map((e) => ({
          exercise_id: null, name: e.name, muscle_group: e.muscle_group, set_type: 'straight', group_id: null,
          reps_scheme: e.reps_scheme, is_time_based: e.is_time_based, sets_count: e.sets_count, rest_seconds: e.rest_seconds,
          tempo: '', youtube_url: '', notes: '',
        })),
      })).filter((s) => s.exercises.length > 0);
      if (sessions.length === 0) { toast.error(L('Gardez au moins un exercice.', 'Keep at least one exercise.')); setBusy(false); return; }
      const res = await authFetch('/api/tracking/programs-pro', {
        method: 'POST',
        body: JSON.stringify({ name: programName.trim(), goal: wizardData.goal, weeks_target: wizardData.durationWeeks, tonnage_growth_target_pct: 5, sessions }),
      });
      if (!res.ok) throw new Error();
      toast.success(L('Programme créé !', 'Program created!'));
      setTimeout(onCreated, 600);
    } catch {
      toast.error(L("Échec de la création.", 'Creation failed.'));
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'wizard') {
    return (
      <div className="space-y-5 relative" data-testid="assisted-builder-wizard">
        <ProgramWizard en={en} mode="assisted" onCancel={onBack} onComplete={onWizardComplete} />
      </div>
    );
  }

  if (phase === 'mode-choice') {
    return (
      <div className="space-y-5" data-testid="assisted-mode-choice">
        <button onClick={() => setPhase('wizard')} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="mode-choice-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour au questionnaire', 'Back to questionnaire')}
        </button>
        <div className="text-center space-y-1.5">
          <h3 className="text-lg font-black text-white">{L('Comment voulez-vous construire votre programme ?', 'How do you want to build your program?')}</h3>
          <p className="text-xs text-zinc-500">{L('Vous pourrez toujours tout modifier plus tard.', 'You can always change everything later.')}</p>
        </div>
        <button onClick={chooseSuggested} data-testid="mode-choice-suggested-btn"
          className="w-full text-left rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-emerald-500/20 hover:border-emerald-400/50 p-5 flex items-center gap-4 transition-colors">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shrink-0"><Sparkles className="w-6 h-6 text-white" /></div>
          <div>
            <p className="font-black text-white">{L('Voir mon programme suggéré', 'See my suggested program')}</p>
            <p className="text-xs text-zinc-400">{L('Le plus rapide : on choisit tout pour vous selon vos réponses.', 'Fastest: we choose everything for you based on your answers.')}</p>
          </div>
        </button>
        <button onClick={() => setPhase('custom')} data-testid="mode-choice-custom-btn"
          className="w-full text-left rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-fuchsia-500/20 hover:border-fuchsia-400/50 p-5 flex items-center gap-4 transition-colors">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-400 to-purple-500 flex items-center justify-center shrink-0"><Wrench className="w-6 h-6 text-white" /></div>
          <div>
            <p className="font-black text-white">{L('Choisir moi-même', 'Choose myself')}</p>
            <p className="text-xs text-zinc-400">{L('Le plus complet : choisissez votre split, vos exercices, séries, repos, tempo...', 'The most complete: choose your split, exercises, sets, rest, tempo...')}</p>
          </div>
        </button>
      </div>
    );
  }

  if (phase === 'custom') {
    return (
      <CustomBeginnerFlow en={en} wizardData={wizardData} onBack={() => setPhase('mode-choice')} onCreated={onCreated} />
    );
  }

  return (
    <div className="space-y-5" data-testid="assisted-builder-review">
      <button onClick={() => setPhase('mode-choice')} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="assisted-back-to-wizard-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-white" /></div>
        <div>
          <h3 className="text-lg font-black text-white">{L('Votre programme suggéré', 'Your suggested program')}</h3>
          <p className="text-xs text-zinc-500">{L('Décochez ou changez un exercice qui ne vous convient pas, puis créez votre programme.', "Uncheck or swap any exercise you don't like, then create your program.")}</p>
        </div>
      </div>

      <div>
        <label className="text-xs text-zinc-500 mb-1 block">{L('Nom du programme', 'Program name')}</label>
        <input value={programName} onChange={(e) => setProgramName(e.target.value)}
          className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm" data-testid="assisted-program-name-input" />
      </div>

      {draft.map((s, sIdx) => (
        <div key={s.session_label} className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(16,185,129,0.25)] p-4 space-y-2.5" data-testid={`assisted-session-${sIdx}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-emerald-300 font-bold text-sm">{s.session_label}</p>
            <span className="inline-flex items-center gap-1 text-[11px] text-zinc-400 font-semibold" data-testid={`assisted-session-time-${sIdx}`}>
              <Clock className="w-3 h-3" /> ≈ {estimateSessionMinutes(s)} min {s.targetMinutes ? `(${L('cible', 'target')} ${s.targetMinutes} min ±${TOLERANCE_MINUTES})` : ''}
            </span>
          </div>
          {s.insufficientPool && (
            <p className="text-[11px] text-amber-300 flex items-center gap-1.5" data-testid={`assisted-insufficient-pool-${sIdx}`}>
              {L('Pas assez d’exercices dispo avec ce matériel pour cette durée — ajoutez-en un manuellement ci-dessous.', 'Not enough exercises available with this equipment for this duration — add one manually below.')}
            </p>
          )}
          <AnimatePresence>
            {s.exercises.map((e) => (
              <motion.div key={e._uid} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className={`flex items-center gap-3 rounded-xl border p-2.5 transition-colors ${e.included ? 'bg-white/[0.04] border-white/10' : 'bg-white/[0.01] border-white/5 opacity-40'}`}
                data-testid={`assisted-exercise-${e._uid}`}>
                <button onClick={() => toggleExercise(sIdx, e._uid)} data-testid={`assisted-toggle-${e._uid}`}
                  className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 border ${e.included ? 'bg-emerald-500 border-emerald-500' : 'border-white/20'}`}>
                  {e.included && <Check className="w-4 h-4 text-black" />}
                </button>
                <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden shrink-0 flex items-center justify-center">
                  {e.image_url || e.video_url ? <ExerciseMedia exercise={e} alt={e.name} autoPlayVideo={false} /> : <ImageOff className="w-4 h-4 text-zinc-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{e.name}</p>
                  <p className="text-[11px] text-zinc-500">{e.muscle_group} · {e.sets_count} × {e.reps_scheme} · {e.rest_seconds}s {L('repos', 'rest')}</p>
                </div>
                {(() => {
                  const usableCount = e._candidates.filter((c) => c.name === e.name || !s.exercises.some((other) => other._uid !== e._uid && other.name === c.name)).length;
                  return usableCount > 1 && (
                    <button onClick={() => swapExercise(sIdx, e._uid)} title={L('Changer cet exercice', 'Swap this exercise')} className="p-2 rounded-lg text-zinc-400 hover:text-emerald-300 hover:bg-emerald-500/10 shrink-0" data-testid={`assisted-swap-${e._uid}`}>
                      <RefreshCcw className="w-3.5 h-3.5" />
                    </button>
                  );
                })()}
              </motion.div>
            ))}
          </AnimatePresence>
          <button onClick={() => setPickerFor(sIdx)} className="w-full mt-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-emerald-500/30 text-emerald-300 text-xs font-bold hover:bg-emerald-500/[0.06] transition-colors" data-testid={`assisted-add-exercise-btn-${sIdx}`}>
            <BookOpen className="w-3.5 h-3.5" /> {L('Ajouter un exercice (bibliothèque)', 'Add an exercise (library)')}
          </button>
        </div>
      ))}

      <button onClick={createProgram} disabled={busy} data-testid="assisted-create-program-btn"
        className="w-full py-4 rounded-full font-black text-white flex items-center justify-center gap-2 transition-transform active:scale-95 hover:scale-[1.02] bg-gradient-to-r from-emerald-400 to-teal-500 shadow-lg hover:shadow-emerald-500/25 disabled:opacity-50">
        {busy ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Wand2 className="w-4.5 h-4.5" />} {L('Créer mon programme', 'Create my program')}
      </button>

      {pickerFor !== null && (
        <LibraryPickerModal en={en} onClose={() => setPickerFor(null)} onAdd={(picked) => addExercise(pickerFor, picked)} />
      )}
    </div>
  );
};

export default AssistedProgramBuilder;
